import { useEffect, useState } from "react";
import { AppProvider, useApp } from "./product/context";
import { PlayerDock, PlayerProvider, usePlayer } from "./product/player";
import { Icon, LanguageSelect, Empty, Modal } from "./product/visual";
import Discover from "./product/Discover";
import WeeklyIntro from "./product/WeeklyIntro";
import EpisodePage from "./product/Episode";
import {
  Account,
  Directory,
  Entity,
  Library,
  Search,
  WhatsNew,
} from "./product/Pages";
import { AppearanceControls, Brand } from "./product/Appearance";
import { brands } from "./product/preferences";
function Shell() {
  const player = usePlayer();
  const {
    catalogue,
    loading,
    error,
    refresh,
    appearance,
    user,
    lang,
    t,
    toast,
    localization,
  } = useApp();
  const [hash, setHash] = useState(location.hash || "#/"),
    [themesOpen, setThemesOpen] = useState(false),
    [navOpen, setNavOpen] = useState(false);
  useEffect(() => {
    const change = () => {
      setHash(location.hash || "#/");
      setNavOpen(false);
      window.scrollTo({ top: 0, behavior: "instant" });
    };
    window.addEventListener("hashchange", change);
    return () => window.removeEventListener("hashchange", change);
  }, []);
  const [path, query] = hash.slice(2).split("?"),
    [section, id] = path.split("/");
  let decoded = "";
  try {
    decoded = decodeURIComponent(id || "");
  } catch {}
  const params = new URLSearchParams(query);
  const at =
    params.has("t") && Number.isFinite(Number(params.get("t")))
      ? Math.max(0, Number(params.get("t")))
      : undefined;
  const ep = catalogue.episodes.find((e) => e.id === decoded);
  useEffect(() => {
    document.title = `${ep ? ep.title : section === "new" ? t("new") : section === "search" ? t("search") : "Adventurous music"} — ${brands[appearance.brand].name}`;
  }, [section, ep, lang, appearance.brand]);
  const links = [
    ["", t("discover")],
    ["collection", t("browse")],
    ["new", t("new")],
    ["shows", t("shows")],
    ["stations", t("stations")],
    ["artists", t("artists")],
  ];
  let page = loading ? (
    <Empty>{t("loading")}</Empty>
  ) : error ? (
    <div className="error-page">
      <p role="alert">{error}</p>
      <button className="button" onClick={refresh}>
        {t("retry")}
      </button>
    </div>
  ) : section === "episode" ? (
    ep ? (
      <EpisodePage episode={ep} at={at} />
    ) : (
      <Empty>{t("noResults")}</Empty>
    )
  ) : section === "new" ? (
    <WhatsNew />
  ) : section === "shows" || section === "stations" || section === "artists" ? (
    <Directory kind={section} />
  ) : section === "show" || section === "station" || section === "artist" ? (
    <Entity kind={section} id={decoded} />
  ) : section === "search" ? (
    <Search initialQuery={params.get("q") || ""} />
  ) : section === "account" ? (
    <Account />
  ) : section === "library" ? (
    <Library />
  ) : section === "collection" ? (
    <Discover />
  ) : !section ? (
    <WeeklyIntro />
  ) : (
    <Empty>
      {t("noResults")} <a href="#/">{t("discover")}</a>
    </Empty>
  );
  return (
    <div
      className={
        "app-shell " +
        (!section
          ? "home-shell "
          : section === "episode"
            ? "player-shell "
            : "") +
        (navOpen ? "nav-open" : "")
      }
    >
      <a
        className="skip-link"
        href="#main"
        onClick={(e) => {
          e.preventDefault();
          document.getElementById("main")?.focus();
        }}
      >
        {t("skipContent")}
      </a>
      <header className="site-header">
        <Brand />
        <nav id="main-navigation" aria-label="Main navigation">
          {links.map(([key, label]) => (
            <a
              key={key}
              href={"#/" + key}
              aria-current={section === key ? "page" : undefined}
            >
              {label}
            </a>
          ))}
          <a className="mobile-player-link" href="#/search">
            {t("search")}
          </a>
          <a className="mobile-player-link" href="#/library">
            {t("library")}
          </a>
          <a className="mobile-player-link" href="#/account">
            {user?.username || t("signin")}
          </a>
        </nav>
        <div className="header-tools">
          <a href="#/search" className="icon-button" aria-label={t("search")}>
            <Icon name="search" />
          </a>
          <LanguageSelect />
          {
            <button
              className="icon-button"
              aria-label={t("appearance")}
              title={t("appearance")}
              onClick={() => setThemesOpen(true)}
            >
              <Icon name="sun" />
            </button>
          }
          <a
            className="icon-button library-icon"
            href="#/library"
            aria-label={t("library")}
          >
            <Icon name="note" />
          </a>
          <button
            className="icon-button mobile-nav-toggle"
            aria-expanded={navOpen}
            aria-controls="main-navigation"
            aria-label={t("navigation")}
            onClick={() => setNavOpen(!navOpen)}
          >
            <Icon name={navOpen ? "close" : "queue"} />
          </button>
          <a
            href="#/account"
            className="account-link"
            aria-label={user?.username || t("signin")}
          >
            <Icon name="user" size={17} />
            <span>{user?.username || t("signin")}</span>
          </a>
        </div>
      </header>
      <main
        id="main"
        tabIndex={-1}
        className="main-content"
        key={section === "episode" ? section + decoded : section}
      >
        <div className="page-enter">{page}</div>
      </main>
      {section && (
        <footer className="site-footer">
          <Brand footer />
          <p>{t("working")}</p>
          <span className="mono">LOCAL EDITION / 01</span>
        </footer>
      )}
      {localization.status === "running" && (
        <div className="translation-status" role="status">
          <span className="live-dot" />
          {t("translating")}
        </div>
      )}
      {localization.status === "unavailable" && user && (
        <div className="translation-status">{t("unavailable")}</div>
      )}
      <PlayerDock
        compact={section === "episode"}
        hidden={
          section === "episode" &&
          player.episode?.id === decoded &&
          player.offscreenEpisode !== decoded
        }
      />
      {toast && (
        <div className="toast" role="status">
          <Icon name="check" size={16} />
          {toast}
        </div>
      )}
      {themesOpen && (
        <Modal title={t("appearance")} onClose={() => setThemesOpen(false)}>
          <AppearanceControls />
        </Modal>
      )}
    </div>
  );
}
export default function App() {
  return (
    <AppProvider>
      <PlayerProvider>
        <Shell />
      </PlayerProvider>
    </AppProvider>
  );
}
